import { useNavigate, useParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'

import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Building,
  Mail,
  MapPin,
  Loader2,
  Check,
  EyeOff,
} from 'lucide-react'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
} from '../../components/StatsBar'
import { AuthService } from '@/services/core/auth'
import { PoliticalEntitiesApi, PoliticalEntityInput } from '@/services/political/politicalEntitiesApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { PoliticalEntityType, PoliticalEntity } from '../../types/models'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { TeamsApi } from '@/services/users/teamsApi'
import { COUNTRIES, CountryFlag } from '../../data/countries.tsx'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate } from '../../utils/format'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'

// ── Inner form component ────────────────────────────────────────────────────

interface PoliticalEntityEditFormProps {
  entity: PoliticalEntity | null
  id: string
  isNew: boolean
}

function PoliticalEntityEditForm({ entity, id, isNew }: PoliticalEntityEditFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const TYPE_OPTIONS: { label: string; value: PoliticalEntityType; icon: string }[] = [
    { label: t('political.typeSponsor'), value: 'SPONSOR', icon: 'pi pi-building' },
    { label: t('political.typePayer'), value: 'PAYER', icon: 'pi pi-wallet' },
  ]

  const LEGAL_ENTITY_OPTIONS = [
    { label: t('political.legalPublic'), value: 'CORPORATE_ENTITY_PUBLIC' },
    { label: t('political.legalPrivate'), value: 'CORPORATE_ENTITY_PRIVATE' },
    { label: t('political.legalParty'), value: 'POLITICAL_PARTY' },
    { label: t('political.legalIndividual'), value: 'INDIVIDUAL' },
  ]

  const hasToken = AuthService.isAuthenticated()
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const { data: entities = [] } = useQuery({
    queryKey: ['politicalEntities'],
    queryFn: () => PoliticalEntitiesApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const controllers = entities.filter((e) => e.type === 'CONTROLLER')

  const form = useForm({
    defaultValues: {
      name: entity?.name ?? '',
      type: entity?.type ?? 'SPONSOR' as PoliticalEntityType,
      description: entity?.description ?? '',
      legalEntity: entity?.legalEntity ?? '',
      contactEmail: entity?.contactEmail ?? '',
      siteUrl: entity?.siteUrl ?? '',
      vatId: entity?.vatId ?? '',
      addressLine1: entity?.address?.addressLine1 ?? '',
      addressLine2: entity?.address?.addressLine2 ?? '',
      city: entity?.address?.city ?? '',
      state: entity?.address?.state ?? '',
      postCode: entity?.address?.postCode ?? '',
      country: entity?.address?.country ?? '',
      ownerTeamId: entity?.ownerTeamId ?? '',
      parentId: entity?.parentId ?? '',
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        type: value.type,
        description: value.description,
        legalEntity: value.legalEntity,
        contactEmail: value.contactEmail,
        siteUrl: value.siteUrl,
        vatId: value.vatId,
        address: {
          addressLine1: value.addressLine1,
          addressLine2: value.addressLine2,
          city: value.city,
          state: value.state,
          postCode: value.postCode,
          country: value.country,
        },
        ownerTeamId: value.ownerTeamId || undefined,
        parentId: value.parentId || undefined,
        objectState: (entity?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => PoliticalEntitiesApi.update(id!, {
      name: entity!.name,
      type: entity!.type,
      description: entity!.description,
      legalEntity: entity!.legalEntity,
      contactEmail: entity!.contactEmail,
      address: entity!.address,
      siteUrl: entity!.siteUrl,
      vatId: entity!.vatId,
      ownerTeamId: entity!.ownerTeamId,
      parentId: entity!.parentId,
      objectState,
    }),
    ['politicalEntity', id],
    ['politicalEntities']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: PoliticalEntityInput) =>
      isNew ? PoliticalEntitiesApi.create(id!, input) : PoliticalEntitiesApi.update(id!, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['politicalEntity', id], data)
      queryClient.setQueryData(['politicalEntities'], (old: PoliticalEntity[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((e) => e.id === data.id)
        return idx >= 0 ? old.map((e) => (e.id === data.id ? data : e)) : [...old, data]
      })
      markClean()
      toast.success(t('common.saved'), {
        description: isNew
          ? t('political.entityCreatedSuccess')
          : t('political.entitySavedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      toast.error(t('common.error'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const crumbs = [
    { label: t('political.entities'), href: '/political/entities' },
    { label: isNew ? t('political.newEntity') : (entity?.name ?? t('political.editEntity')) },
  ]

  return (
    <AppShell
      crumbs={crumbs}
      showBack
      objectType={isNew ? undefined : t('objectTypes.politicalEntity')}
    >
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.politicalEntity')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('political.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
            {isNew && <p>{t('political.newEntitySubtitle')}</p>}
          </div>
          {entity && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={entity!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {entity && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={entity.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(entity.createTime)}
                    updated={formatDate(entity.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <ValidationWarnings warnings={entity?.validationWarnings} />
        {entity?.visibilityType && entity.visibilityType !== 'STANDARD' && (
          <Alert variant="warning">
            <EyeOff />
            <AlertTitle>{t('political.visibilityNotPublicTitle')}</AlertTitle>
            <AlertDescription>
              {t('political.visibilityNotPublic', { visibility: t(`political.visibilityTypes.${entity.visibilityType}`) })}
            </AlertDescription>
          </Alert>
        )}

        <div className="card-row">
        {/* Identity card */}
        <Card className="flex-1 min-w-0">
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Building} />
              {t('political.identity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="field-grid">
              {entity?.type !== 'CONTROLLER' && (
                <div className="p-field">
                  <label>{t('common.type')}</label>
                  <form.Field name="type">
                    {(field) => (
                      <Select label={t('common.type')} value={field.state.value} onValueChange={(v) => field.handleChange(v as typeof field.state.value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('political.selectType')}>{TYPE_OPTIONS.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
              )}

              <div className="p-field">
                <label>{t('political.legalEntity')}</label>
                <form.Field name="legalEntity">
                  {(field) => (
                    <Select label={t('political.legalEntity')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('political.selectLegalEntity')}>{LEGAL_ENTITY_OPTIONS.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__clear__">{t('common.none', '— None —')}</SelectItem>
                        {LEGAL_ENTITY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </form.Field>
              </div>

              <div className="p-field full">
                <label>{t('common.description')}</label>
                <form.Field name="description">
                  {(field) => (
                    <Textarea
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('advertisers.descriptionPlaceholder')}
                      rows={2}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('political.ownerTeam')}</label>
                <form.Field name="ownerTeamId">
                  {(field) => (
                    <Select label={t('political.ownerTeam')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('political.selectTeam')}>{teams.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__clear__">{t('common.none', '— None —')}</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </form.Field>
              </div>

              {entity?.type !== 'CONTROLLER' && (
                <div className="p-field">
                  <label>{t('political.parentController')}</label>
                  <form.Field name="parentId">
                    {(field) => (
                      <Select label={t('political.parentController')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('political.selectController')}>{controllers.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__clear__">{t('common.none', '— None —')}</SelectItem>
                          {controllers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact & Address card */}
        <Card tone="people" className="flex-1 min-w-0">
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Mail} />
              {t('advertisers.contact')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="field-grid field-grid--3-responsive">
              <div className="p-field">
                <label>{t('political.contactEmail')}</label>
                <form.Field name="contactEmail">
                  {(field) => (
                    <Input
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('political.contactEmailPlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('common.website')}</label>
                <form.Field name="siteUrl">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('political.websitePlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('political.vatId')}</label>
                <form.Field name="vatId">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('political.vatIdPlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-(--border)">
              <div className="flex items-center gap-2 mb-3 text-sm font-medium text-(--muted-foreground)">
                <MapPin className="size-3.5" />
                {t('address.title')}
              </div>
              <div className="field-grid field-grid--3-responsive">
              <div className="p-field">
                <label>{t('address.line1')}</label>
                <form.Field name="addressLine1">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.streetPlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('address.line2')}</label>
                <form.Field name="addressLine2">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.aptPlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('address.city')}</label>
                <form.Field name="city">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.cityPlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('address.stateRegion')}</label>
                <form.Field name="state">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.statePlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('address.postCode')}</label>
                <form.Field name="postCode">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.postCodePlaceholder')}
                      style={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </div>

              <div className="p-field">
                <label>{t('address.country')}</label>
                <form.Field name="country">
                  {(field) => (
                    <Select label={t('address.country')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('address.selectCountry')}>{COUNTRIES.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__clear__">{t('common.none', '— None —')}</SelectItem>
                        {COUNTRIES.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <CountryFlag code={opt.value} />
                              {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </form.Field>
              </div>
            </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Save bar */}
        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/political/entities')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              {isNew ? <Check className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('political.createEntity') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function PoliticalEntityEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const hasToken = AuthService.isAuthenticated()

  const {
    data: entity,
    isLoading: loadingEntity,
    isError,
  } = useQuery({
    queryKey: ['politicalEntity', id],
    queryFn: () => PoliticalEntitiesApi.getById(id!),
    enabled: !!id && hasToken,
    retry: false,
  })

  const isNew = !loadingEntity && entity === null

  const crumbsLoading = [
    { label: t('political.entities'), href: '/political/entities' },
    { label: t('political.editEntity') },
  ]

  if (isError) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (loadingEntity) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <PoliticalEntityEditForm
      key={id}
      entity={entity ?? null}
      id={id!}
      isNew={isNew}
    />
  )
}
